import { FC, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { TransactionDetailInfo } from './transactionDetail.types';
import { useTranslation } from 'react-i18next';
import useFetchClientCreditCards from '@/hooks/useApi/useFetchClientCreditCards';
import useFetchTransactionsHistory from '@/hooks/useApi/useFetchTransactionsHistory';
import {
  Box,
  formattedCurrency,
  CreditCardTransactionResponse,
  ClientCreditCardLists,
  Card,
  withStyles,
  capitalizeFirstLetter,
  TransactionType,
} from 'vopay';
import { TransactionCard, Title, DetailsRows, TransactionStatus } from 'UI';
import ApprovalHistoryDetailsCard from './Card/ApprovalHistoryDetailsCard';
import styles from '../Transactions.module.scss';
import { useClientData } from '@/providers/ClientData.provider';
import useElinxDetails from './hooks/useElinxDetails';

const cn = withStyles(styles);

type CreditCardTransactionInfoProps = {
  transaction: CreditCardTransactionResponse;
  isParent?: boolean;
  jwt: string;
  eLinxRequestID?: string;
  transactionDetail: TransactionDetailInfo;
};

const CreditCardTransactionInfo: FC<CreditCardTransactionInfoProps> = ({
  transaction,
  isParent = false,
  jwt,
  eLinxRequestID = '',
  transactionDetail,
}) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.CreditCardTransactionInfo',
  });

  const { t: tGlobal } = useTranslation('translations');

  const {
    response: { JWT: mainJwt },
  } = useClientData();

  const isMainAccount = !jwt || jwt === mainJwt;
  const { failureReason, accountId, hasErrorInfo } = transactionDetail;
  const currentDate = moment().utc().format('YYYY-MM-DD');
  const { creditCards, fetchCreditCards } = useFetchClientCreditCards({ bearer: jwt });
  const { transactions, fetchTransactionsHistory } = useFetchTransactionsHistory();
  const [isGetNonClientControlled, setIsGetNonClientControlled] = useState(false);

  const [creditCardsList, setCreditCardsList] = useState<{
    [idx: number]: ClientCreditCardLists;
  }>({});

  const creditCardInformation = creditCardsList[0] || {};

  useEffect(() => {
    fetchTransactionsHistory({
      StartDateTime: moment().subtract(5, 'years').format('YYYY-MM-DD'),
      EndDateTime: currentDate,
      TransactionID: transaction.TransactionID,
      IncludeSubaccountTransactions: true,
    }).then(() => {
      if (transaction.CreditCardToken !== '' && !isGetNonClientControlled) {
        fetchCreditCards({
          PortalUser: '',
          ClientControlled: true,
          CreditCardToken: transaction.CreditCardToken,
        });

        setIsGetNonClientControlled(true);
      }
    });
  }, [transaction.TransactionID, currentDate]);

  useEffect(() => {
    if (isGetNonClientControlled) {
      fetchCreditCards({
        PortalUser: '',
        ClientControlled: false,
        CreditCardToken: transaction.CreditCardToken,
      });

      setIsGetNonClientControlled(false);
    }

    setCreditCardsList({ ...creditCardsList, ...creditCards });
  }, [creditCards]);

  const { elinxAdditionalId, invoiceButton } = useElinxDetails({ ELinxRequestID: eLinxRequestID, jwt });

  const transactionDetails = [
    {
      label: t('TransactionDetails.transactionStatus', { defaultValue: 'Transaction Status' }),
      value: (
        <Box jc="fe">
          <TransactionStatus
            status={transaction.TransactionStatus}
            label={capitalizeFirstLetter(
              tGlobal(`Transaction.TransactionHistory.statuses.${transaction.TransactionStatus}`, {
                defaultValue: transaction.TransactionStatus,
              }),
            )}
          />
        </Box>
      ),
      visible: isParent,
    },
    {
      label: t('TransactionDetails.type', { defaultValue: 'Transaction Type' }),
      value: t('creditCard'),
    },
    {
      label: t('TransactionDetails.transactionTypeCode', { defaultValue: 'Transaction Type Code' }),
      value: transaction.TransactionTypeCode || '-',
    },
    {
      label: t('TransactionDetails.transactionDate', { defaultValue: 'Transaction Date' }),
      value: transaction.TransactionDateTime,
    },
    {
      label: t('TransactionDetails.lastModified', { defaultValue: 'Last Modified' }),
      value: transaction.LastModified,
    },
    {
      label: t('TransactionDetails.country', { defaultValue: 'Country' }),
      value: transaction.Country || '-',
    },
    {
      label: t('TransactionDetails.glCode', { defaultValue: 'GL Code' }),
      value: transaction.GLCode || '-',
    },
    {
      label: t('TransactionDetails.invoice', { defaultValue: 'Invoice' }),
      value: invoiceButton,
      visible: !!invoiceButton,
    },
    {
      label: t('ErrorInfo.failureReason', { defaultValue: 'Failure Reason' }),
      value: failureReason || '-',
      visible: isParent && failureReason !== '',
    },
  ];

  const cardDetails = [
    {
      label: t('CardDetails.token', { defaultValue: 'Credit Card Token' }),
      value: transaction.CreditCardToken || '-',
    },
  ];

  const errorDetails = [
    {
      label: t('ErrorInfo.failureDateTime', { defaultValue: 'Failure Date Time' }),
      value: transaction.LastModified,
    },
    {
      label: t('ErrorInfo.amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency} ${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('ErrorInfo.failureReason', { defaultValue: 'Failure Reason' }),
      value: failureReason || '-',
      visible: !isParent,
    },
  ];

  const fullName =
    creditCardInformation.CreditCardHolderName ||
    transactions
      .filter((x) => x.TransactionID === transaction.TransactionID)
      .map((data) => data.FullName)
      .pop() ||
    '-';

  // Credit Card funding also creates a sibling Fee transaction as a child, so the real payment is
  // whichever child isn't a Fee. Already fetched above (its Parent_ID points directly at this
  // transaction, so the generic history endpoint returns it in the same call).
  const subTransaction = useMemo(
    () =>
      transactions.find(
        (t) => t.ParentTransactionID === transaction.TransactionID && t.TransactionType !== TransactionType.FEE,
      ),
    [transactions, transaction.TransactionID],
  );

  return (
    <Box flx="col" gapSize="24px">
      <TransactionCard
        receiverName={fullName}
        amount={transaction.Amount}
        currency={transaction.Currency}
        transactionId={transaction.TransactionID}
        type={'Credit Card'}
        failureReason={failureReason}
        contactId={accountId}
        jwt={jwt}
        isMainAccount={isMainAccount}
        additionalId={elinxAdditionalId}
        subTransactionId={subTransaction?.TransactionID}
        subTransactionType={subTransaction?.TransactionType}
        subTransactionStatus={subTransaction?.TransactionStatus}
      />

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('transactionDetailsTitle', { defaultValue: 'Transaction Details' })}
        </Title>

        <DetailsRows className={cn('rows')} details={transactionDetails} />
      </Card>

      <ApprovalHistoryDetailsCard
        transactionID={transaction.TransactionID}
        status={transaction.TransactionStatus}
        type={TransactionType.CREDIT_CARD}
        jwt={jwt}
      />

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('cardDetailsTitle', { defaultValue: 'Credit Card Details' })}
        </Title>

        <DetailsRows className={cn('rows')} details={cardDetails} />
      </Card>

      {hasErrorInfo && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('errorInfoTitle', { defaultValue: 'Error Information' })}
          </Title>

          <DetailsRows className={cn('rows')} details={errorDetails} />
        </Card>
      )}
    </Box>
  );
};

export default CreditCardTransactionInfo;
